import {LoadingButton} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useState,
} from 'react';

import API from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  ids?: number[] | null;
  commtrac_node_ids?: number[] | null;
  buttonTitle?: string;
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  onDone?: () => void;
}

const EmployeeItemsPurgeChatEventsButton = <
  T extends ComponentType = typeof Button,
>({
  ids = null,
  commtrac_node_ids = null,
  buttonTitle,
  component,
  componentProps,
  children,
  onDone,
}: Props<T>) => {
  const Component = component ?? Button;
  const {enqueueSnackbar} = useSnackbar();

  const [isOpened, setIsOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => setIsOpened(true);
  const closeModal = () => setIsOpened(false);

  const submit = async () => {
    setIsSubmitting(true);

    const data: {
      action: string;
      chatOnly: true;
      asset_human_ids?: number[];
      commtrac_node_ids?: number[];
    } = {
      action: 'data',
      chatOnly: true,
    };

    if (ids) {
      data.asset_human_ids = ids;
    } else if (commtrac_node_ids) {
      data.commtrac_node_ids = commtrac_node_ids;
    }

    try {
      await API.post(`${apiBaseUrl}/purge`, data);
      const message = 'Chat purged successfully';
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onDone?.();
      closeModal();
    } catch (error: any) {
      const message = error?.response?.data?.message ?? 'There is an error';
      enqueueSnackbar(message, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Component {...componentProps} onClick={openModal}>
        {children}
      </Component>

      <Dialog open={isOpened} onClose={closeModal}>
        <DialogTitle>Purge Chat</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to purge all chat data from the selected
            device?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsOpened(false)}>Cancel</Button>

          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            autoFocus
            onClick={() => submit()}
            color="error"
          >
            {buttonTitle ?? `Purge Chat`}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmployeeItemsPurgeChatEventsButton;
